import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { toast } from "sonner";
import { Calendar, Save, ChevronLeft, ChevronRight, Pause, Trash2 } from "lucide-react";
import { dayOverlapsRanges, parseYMDLocal } from "../lib/dateLocal";

function pad(n) {
  return n < 10 ? `0${n}` : `${n}`;
}

function ymdLocal(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function monthGrid(year, month) {
  const first = new Date(year, month, 1, 12, 0, 0, 0);
  const start = new Date(first);
  start.setDate(start.getDate() - start.getDay());
  const days = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push({
      ymd: ymdLocal(d),
      inMonth: d.getMonth() === month,
    });
  }
  return days;
}

function formatMonthLabel(year, month) {
  return new Date(year, month, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export default function HostCalendarPanel({ vehicleId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calendarMode, setCalendarMode] = useState("block_unavailable");
  const [selected, setSelected] = useState(new Set());
  const [booked, setBooked] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [viewDate, setViewDate] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [pauseFrom, setPauseFrom] = useState("");
  const [resumeOn, setResumeOn] = useState("");
  const [scheduleBusy, setScheduleBusy] = useState(false);

  const gridDays = useMemo(
    () => monthGrid(viewDate.year, viewDate.month),
    [viewDate.year, viewDate.month],
  );

  const load = async () => {
    if (!vehicleId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/vehicles/${vehicleId}/host-calendar`, { withCredentials: true });
      const mode = r.data?.calendar_mode || "block_unavailable";
      setCalendarMode(mode);
      setSelected(new Set(r.data?.calendar_days || r.data?.blocked_days || []));
      setBooked(r.data?.booked_ranges || []);
      setSchedules(r.data?.listing_schedules || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load calendar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [vehicleId]);

  const toggleDay = (day) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  const saveCalendar = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/vehicles/${vehicleId}/blocked-days`,
        { days: [...selected].sort(), calendar_mode: calendarMode },
        { withCredentials: true },
      );
      toast.success("Rentable calendar updated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addSchedule = async () => {
    if (!pauseFrom) {
      toast.error("Choose a pause start date");
      return;
    }
    setScheduleBusy(true);
    try {
      await axios.post(
        `${API}/vehicles/${vehicleId}/listing-schedule`,
        { pause_from: pauseFrom, resume_on: resumeOn || null },
        { withCredentials: true },
      );
      toast.success("Listing pause scheduled");
      setPauseFrom("");
      setResumeOn("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not schedule pause");
    } finally {
      setScheduleBusy(false);
    }
  };

  const cancelSchedule = async (scheduleId) => {
    setScheduleBusy(true);
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}/listing-schedule/${scheduleId}`, { withCredentials: true });
      toast.success("Schedule cancelled");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally {
      setScheduleBusy(false);
    }
  };

  const shiftMonth = (delta) => {
    setViewDate((prev) => {
      const d = new Date(prev.year, prev.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  if (!vehicleId) return null;

  const isRentableMode = calendarMode === "define_rentable";

  return (
    <div className="zc-card p-5 md:p-6" data-testid="host-calendar-panel">
      <div className="flex items-center gap-2 mb-1">
        <Calendar size={14} className="text-[var(--zc-accent)]" />
        <div className="eyebrow">Rentable calendar</div>
      </div>
      <p className="text-xs text-[var(--zc-text-3)] mb-4">
        {isRentableMode
          ? "Mark the days your car is available to rent. All other days are hidden from search."
          : "Tap days to block when your vehicle is not available. Booked trips stay reserved automatically."}
      </p>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="host-calendar-mode">
        <button
          type="button"
          onClick={() => setCalendarMode("block_unavailable")}
          className={
            "px-3 h-8 text-xs font-semibold rounded-md border "
            + (calendarMode === "block_unavailable"
              ? "border-[var(--zc-accent)] text-[var(--zc-accent)] bg-[var(--zc-accent-soft)]"
              : "border-[var(--zc-border)] text-[var(--zc-text-3)]")
          }
        >
          Block unavailable days
        </button>
        <button
          type="button"
          onClick={() => setCalendarMode("define_rentable")}
          className={
            "px-3 h-8 text-xs font-semibold rounded-md border "
            + (calendarMode === "define_rentable"
              ? "border-[var(--zc-accent)] text-[var(--zc-accent)] bg-[var(--zc-accent-soft)]"
              : "border-[var(--zc-border)] text-[var(--zc-text-3)]")
          }
        >
          Define rentable days only
        </button>
      </div>

      <div className="flex items-center justify-between gap-3 mb-3">
        <button type="button" onClick={() => shiftMonth(-1)} className="p-2 zc-btn-ghost" aria-label="Previous month" data-testid="host-cal-prev">
          <ChevronLeft size={16} />
        </button>
        <div className="text-sm font-semibold">{formatMonthLabel(viewDate.year, viewDate.month)}</div>
        <button type="button" onClick={() => shiftMonth(1)} className="p-2 zc-btn-ghost" aria-label="Next month" data-testid="host-cal-next">
          <ChevronRight size={16} />
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--zc-text-3)]">Loading calendar…</div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1.5 text-center text-2xs uppercase tracking-widest text-[var(--zc-text-3)] mb-2">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {gridDays.map(({ ymd, inMonth }) => {
              const isSelected = selected.has(ymd);
              const dayDate = parseYMDLocal(ymd);
              const isBooked = Boolean(dayDate && dayOverlapsRanges(dayDate, booked));
              const active = isRentableMode ? isSelected : isSelected;
              return (
                <button
                  key={ymd}
                  type="button"
                  disabled={isBooked || !inMonth}
                  onClick={() => !isBooked && inMonth && toggleDay(ymd)}
                  className={
                    "h-9 rounded-md text-xs font-semibold transition "
                    + (!inMonth ? "opacity-30 "
                      : isBooked ? "bg-[var(--zc-bg-3)] text-[var(--zc-text-3)] cursor-not-allowed line-through "
                      : isRentableMode
                        ? (active
                          ? "bg-[rgba(16,185,129,0.14)] text-[var(--zc-success)] border border-[rgba(16,185,129,0.35)]"
                          : "bg-white border border-[var(--zc-border)] text-[var(--zc-text-3)] hover:border-[var(--zc-accent)]")
                        : (active
                          ? "bg-[rgba(220,38,38,0.12)] text-[var(--zc-danger,#dc2626)] border border-[rgba(220,38,38,0.25)]"
                          : "bg-white border border-[var(--zc-border)] hover:border-[var(--zc-accent)]"))
                  }
                  data-testid={`host-cal-${ymd}`}
                  title={isBooked ? "Booked" : isRentableMode ? (active ? "Rentable" : "Not rentable") : (active ? "Blocked" : "Available")}
                >
                  {ymd.slice(8)}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 mt-4 text-2xs text-[var(--zc-text-3)]">
            {isRentableMode ? (
              <>
                <span><span className="inline-block w-3 h-3 rounded bg-[rgba(16,185,129,0.14)] border border-[rgba(16,185,129,0.35)] mr-1 align-middle" /> Rentable</span>
                <span><span className="inline-block w-3 h-3 rounded border border-[var(--zc-border)] mr-1 align-middle" /> Not marked (unavailable)</span>
              </>
            ) : (
              <>
                <span><span className="inline-block w-3 h-3 rounded border border-[var(--zc-border)] mr-1 align-middle" /> Available</span>
                <span><span className="inline-block w-3 h-3 rounded bg-[rgba(220,38,38,0.12)] border border-[rgba(220,38,38,0.25)] mr-1 align-middle" /> Blocked by you</span>
              </>
            )}
            <span><span className="inline-block w-3 h-3 rounded bg-[var(--zc-bg-3)] mr-1 align-middle" /> Booked</span>
          </div>
          <button
            type="button"
            onClick={saveCalendar}
            disabled={saving}
            className="mt-4 inline-flex items-center gap-2 px-4 h-10 zc-btn-primary text-sm"
            data-testid="host-calendar-save"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save calendar"}
          </button>
        </>
      )}

      <div className="mt-8 pt-6 border-t border-[var(--zc-border)]" data-testid="host-listing-schedule">
        <div className="flex items-center gap-2 mb-1">
          <Pause size={14} className="text-[var(--zc-warn,#f59e0b)]" />
          <div className="eyebrow">Schedule listing pause</div>
        </div>
        <p className="text-xs text-[var(--zc-text-3)] mb-3">
          Pause your listing on a future date without doing it manually. Optionally set a resume date to go live again.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 max-w-lg">
          <label className="block text-xs">
            <span className="text-[var(--zc-text-3)]">Pause from</span>
            <input type="date" value={pauseFrom} onChange={(e) => setPauseFrom(e.target.value)} className="zc-input h-10 px-3 w-full mt-1 text-sm" data-testid="host-schedule-pause-from" />
          </label>
          <label className="block text-xs">
            <span className="text-[var(--zc-text-3)]">Resume on (optional)</span>
            <input type="date" value={resumeOn} onChange={(e) => setResumeOn(e.target.value)} className="zc-input h-10 px-3 w-full mt-1 text-sm" data-testid="host-schedule-resume-on" />
          </label>
        </div>
        <button
          type="button"
          onClick={addSchedule}
          disabled={scheduleBusy}
          className="mt-3 px-4 h-9 zc-btn-ghost text-xs font-semibold"
          data-testid="host-schedule-add"
        >
          {scheduleBusy ? "Saving…" : "Add scheduled pause"}
        </button>

        {schedules.length > 0 && (
          <div className="mt-4 space-y-2">
            {schedules.map((s) => (
              <div key={s.schedule_id} className="flex flex-wrap items-center justify-between gap-2 text-xs p-3 rounded-lg border border-[var(--zc-border)] bg-[var(--zc-bg-2)]" data-testid={`host-schedule-row-${s.schedule_id}`}>
                <div>
                  <span className="font-semibold capitalize">{s.status || "pending"}</span>
                  {" · Pause "}{s.pause_from}
                  {s.resume_on ? ` → resume ${s.resume_on}` : " (until you resume manually)"}
                </div>
                {!["completed", "cancelled"].includes(s.status) && (
                  <button type="button" onClick={() => cancelSchedule(s.schedule_id)} disabled={scheduleBusy} className="inline-flex items-center gap-1 text-[var(--zc-text-3)] hover:text-[var(--zc-danger,#dc2626)]" data-testid={`host-schedule-cancel-${s.schedule_id}`}>
                    <Trash2 size={12} /> Cancel
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
